import React, { useEffect, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Range, getTrackBackground } from 'react-range'
import { Field, Formik, FormikErrors } from 'formik'
import { useTheme } from 'styled-components'
import debounce from 'lodash/debounce'
import { Form, Theme } from 'lib/ui'
import { InterceptChangeEvent } from 'lib/ui/components/Form/types'
import { RangeContainer, InputContainer } from './styled'
import { FilterOptionType } from 'lib/modules/catalog/hooks'

interface Props {
  isFetching?: boolean
  onChange: (value: string) => void
  value?: string
  minRange: number
  maxRange: number
  autoSubmit?: boolean
  debounceDelay?: number
}

export const LayeredPriceFilter = ({
  onChange,
  value,
  isFetching,
  minRange,
  maxRange,
  autoSubmit,
  debounceDelay = 1000
}: Props) => {
  const intl = useIntl()
  const theme = useTheme() as Theme
  const [state, setState] = useState(value)
  const parsedValue = getValue(minRange, maxRange, state)
  const handleOnChange = useRef(debounce(onChange, debounceDelay)).current

  useEffect(() => {
    setState(value)
  }, [setState, value])

  const handleMinInputInterceptChange = (e: InterceptChangeEvent<string>) => {
    if (isFetching) {
      return true
    }

    const value = e.value
    const [, max] = parsedValue

    if (!/^[0-9]*$/.test(value)) {
      return true
    }

    setState([value, max].join('_'))
  }

  const handleMaxInputInterceptChange = (e: InterceptChangeEvent<string>) => {
    if (isFetching) {
      return true
    }

    const value = e.value
    const [min] = parsedValue

    if (!/^[0-9]*$/.test(value)) {
      return true
    }

    setState([min, value].join('_'))
  }

  const handleFormSubmit = (values: Values) => {
    const value = `${values.min}_${values.max}`
    if (autoSubmit) {
      handleOnChange(value)
    } else {
      onChange(value)
    }
  }

  return (
    <Formik
      initialValues={{
        min: `${parsedValue[0]}`,
        max: `${parsedValue[1]}`
      }}
      onSubmit={handleFormSubmit}
      validate={handleValidate({ intl, minRange, maxRange })}
    >
      {({ setFieldValue }) => {
        return (
          <Form.Wrapper>
            <Form.FormikObserver
              autoSubmit={autoSubmit}
            />
            <RangeContainer>
              <Range
                values={parsedValue}
                step={getStep(parsedValue)}
                min={minRange}
                max={maxRange}
                onChange={(e) => {
                  setState(e.join('_'))
                  setFieldValue('min', `${e[0]}`)
                  setFieldValue('max', `${e[1]}`)
                }}
                renderTrack={({ props, children }) => (
                  <div
                    onMouseDown={props.onMouseDown}
                    onTouchStart={props.onTouchStart}
                    className='range'
                    style={{
                      ...props.style,
                      height: '25px',
                      display: 'flex',
                      width: '100%'
                    }}
                  >
                    <div
                      ref={props.ref}
                      style={{
                        height: '5px',
                        width: '100%',
                        borderRadius: '0',
                        background: getTrackBackground({
                          values: parsedValue,
                          colors: [theme.palette.general.greyLight, theme.palette.general.grey, theme.palette.general.greyLight],
                          min: minRange,
                          max: maxRange
                        }),
                        alignSelf: 'center'
                      }}
                    >
                      {children}
                    </div>
                  </div>
                )}
                renderThumb={({ props }) => {
                  return (
                    <div
                      {...props}
                      style={{
                        ...props.style,
                        outline: 'none',
                        height: '25px',
                        width: '25px',
                        borderRadius: '0',
                        backgroundColor: theme.palette.general.light,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: `1px solid ${theme.palette.general.grey}`
                      }}
                    >
                      <div
                        style={{
                          height: '10px',
                          width: '3px',
                          backgroundColor: theme.palette.general.grey
                        }}
                      />
                    </div>
                  )
                }}
              />
            </RangeContainer>

            <InputContainer autoSubmit={autoSubmit}>
              <Field
                name='min'
                autoComplete='off'
                label={intl.formatMessage({
                  id: 'catalog.layeredNav.filter.price.label.min'
                })}
                component={Form.Input}
                interceptChange={handleMinInputInterceptChange}
                hasAutoPlaceholder
              />

              <Field
                name='max'
                autoComplete='off'
                label={intl.formatMessage({
                  id: 'catalog.layeredNav.filter.price.label.max'
                })}
                component={Form.Input}
                interceptChange={handleMaxInputInterceptChange}
                hasAutoPlaceholder
              />

              {!autoSubmit && (
                <button type='submit'>
                  {intl.formatMessage({
                    id: 'catalog.layeredNav.filter.price.action.submit'
                  })}
                </button>
              )}
            </InputContainer>
          </Form.Wrapper>
        )
      }}
    </Formik>
  )
}

export interface Values {
  min: string
  max: string
}

const getStep = (values: number[]) => {
  const [min, max] = values
  const diff = max - min
  if (diff < 1000) return 10
  if (diff < 5000) return 100

  return 1000
}

const getValue = (minRange: number, maxRange: number, value?: string): [number, number] => {
  let [min, max] = value?.split('_') ?? []

  if (!min || (parseFloat(min) < minRange)) {
    min = minRange.toString()
  }

  if (!max || (parseFloat(max) > maxRange)) {
    max = maxRange.toString()
  }

  return [parseFloat(min), parseFloat(max)]
}

export const handleValidate = (deps: {
  intl: IntlShape
  minRange: number
  maxRange: number
}) => (values: Values) => {
  const { intl } = deps
  const errors: FormikErrors<Values> = {}
  const inputMin = parseFloat(values.min)
  const inputMax = parseFloat(values.max)

  if (!values.min) {
    errors.min = intl.formatMessage({ id: 'catalog.layeredNav.filter.price.validation.requiredValue' })
  }

  if (!values.max) {
    errors.max = intl.formatMessage({ id: 'catalog.layeredNav.filter.price.validation.requiredValue' })
  }

  if (inputMin > inputMax) {
    errors.min = intl.formatMessage({ id: 'catalog.layeredNav.filter.price.validation.invalidValue' })
  }

  return errors
}

export const getPriceFilterRange = (items: FilterOptionType[]) => {
  const priceRange = items[0].value.split('-')
  const min = parseInt(priceRange[0])
  const max = parseInt(priceRange[1])

  if (isNaN(min) || isNaN(max) || min === max) {
    return
  }

  return [min, max]
}
